/**
 * Enhanced date serialization utilities for grid filters and API calls
 * Prevents [object Object] serialization issues and provides type safety
 */
import type { CompositeFilterDescriptor, DataSourceRequestState, FilterDescriptor } from '@progress/kendo-data-query';
import { State } from '@progress/kendo-data-query';

import type { DateRangeFilterValue } from '../ui/DateRangeFilter/DateRangeFilterProps';

export interface SerializableDateRange {
  type: string;
  fromDate?: string | null;
  toDate?: string | null;
  label?: string;
}

export interface DateRangeResult {
  fromDate: Date | null;
  toDate: Date | null;
  label: string;
}

/**
 * Safely converts a value to a Date object with comprehensive error handling
 */
export function safeToDate(value: unknown): Date | null {
  if (value == null) {
    return null;
  }

  // Already a Date object
  if (value instanceof Date) {
    if (isNaN(value.getTime())) {
      console.warn('safeToDate - Invalid Date object, returning null');
      return null;
    }
    return value;
  }

  // String input
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return null;
    }

    // Check for malformed patterns that should never be dates
    const malformedPatterns = ['[object Object]', 'undefined', 'null', 'NaN'];
    if (malformedPatterns.some((pattern) => trimmed.includes(pattern))) {
      console.warn(`safeToDate - Malformed date string detected: "${trimmed}"`);
      return null;
    }

    const date = new Date(trimmed);
    if (isNaN(date.getTime())) {
      console.warn(`safeToDate - Could not parse date string: "${trimmed}"`);
      return null;
    }
    return date;
  }

  // Number input (timestamp)
  if (typeof value === 'number') {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      console.warn(`safeToDate - Invalid timestamp: ${value}`);
      return null;
    }
    return date;
  }

  // Object that might contain date information
  if (typeof value === 'object' && value !== null) {
    // Check if it's a DateRangeFilterValue that got serialized incorrectly
    if ('type' in value && typeof (value as Record<string, unknown>).type === 'string') {
      console.warn('safeToDate - Found DateRangeFilterValue object instead of Date, this should be handled by transformDateRangeValue');
      return null;
    }

    // Try to extract date from object properties
    const obj = value as Record<string, unknown>;
    if (typeof obj.getTime === 'function') {
      // Looks like a Date-like object
      try {
        const time = (obj.getTime as () => unknown)();
        if (typeof time === 'number' && !isNaN(time)) {
          return new Date(time);
        }
      } catch {
        // Ignore errors
      }
    }
  }

  console.warn(`safeToDate - Unsupported value type for date conversion: ${typeof value}`, value);
  return null;
}

/**
 * Calculates actual date range from a DateRangeFilterValue
 */
export function calculateDateRange(value: DateRangeFilterValue): DateRangeResult {
  const now = new Date();

  switch (value.type) {
    case 'today': {
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      return { fromDate: start, toDate: end, label: 'Today' };
    }

    case 'yesterday': {
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      const start = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
      const end = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59, 999);
      return { fromDate: start, toDate: end, label: 'Yesterday' };
    }

    case 'last-7-days': {
      const start = new Date(now);
      start.setDate(start.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      return { fromDate: start, toDate: end, label: 'Last 7 Days' };
    }

    case 'last-30-days': {
      const start = new Date(now);
      start.setDate(start.getDate() - 29);
      start.setHours(0, 0, 0, 0);
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      return { fromDate: start, toDate: end, label: 'Last 30 Days' };
    }

    case 'this-month': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      return { fromDate: start, toDate: end, label: 'This Month' };
    }

    case 'last-month': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
      return { fromDate: start, toDate: end, label: 'Last Month' };
    }

    case 'custom':
    default: {
      const fromDate = safeToDate(value.fromDate);
      const toDate = safeToDate(value.toDate);

      return { fromDate, toDate, label: 'Custom Range' };
    }
  }
}

/**
 * Transforms various date range representations into a standardized DateRangeFilterValue
 */
export function transformDateRangeValue(value: unknown): DateRangeFilterValue | null {
  if (value == null) {
    return null;
  }

  // Already a DateRangeFilterValue
  if (typeof value === 'object' && value !== null && 'type' in value) {
    const range = value as Record<string, unknown>;
    const validTypes = ['custom', 'today', 'yesterday', 'last-7-days', 'last-30-days', 'this-month', 'last-month'];
    const type = typeof range.type === 'string' && validTypes.includes(range.type) ? (range.type as DateRangeFilterValue['type']) : 'custom';

    return {
      type,
      fromDate: safeToDate(range.fromDate),
      toDate: safeToDate(range.toDate),
    };
  }

  // String that might be a serialized date range
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return transformDateRangeValue(parsed);
    } catch {
      // Not JSON, might be a date string
      const date = safeToDate(value);
      if (date) {
        return {
          type: 'custom',
          fromDate: date,
          toDate: date,
        };
      }
    }
  }

  // Single Date object
  if (value instanceof Date) {
    return {
      type: 'custom',
      fromDate: value,
      toDate: value,
    };
  }

  console.warn('transformDateRangeValue - Could not transform value:', value);
  return null;
}

/**
 * Converts DateRangeFilterValue to individual FilterDescriptor objects
 */
export function dateRangeToFilterDescriptors(field: string, value: DateRangeFilterValue): FilterDescriptor[] {
  const range = calculateDateRange(value);
  const filters: FilterDescriptor[] = [];

  if (range.fromDate) {
    filters.push({
      field,
      operator: 'gte',
      value: range.fromDate,
    });
  }

  if (range.toDate) {
    filters.push({
      field,
      operator: 'lte',
      value: range.toDate,
    });
  }

  return filters;
}

/**
 * Converts FilterDescriptor objects back to DateRangeFilterValue
 */
export function filterDescriptorsToDateRange(field: string, filters: FilterDescriptor[]): DateRangeFilterValue | null {
  const fieldFilters = filters.filter((f) => f.field === field);
  if (fieldFilters.length === 0) {
    return null;
  }

  const gteFilter = fieldFilters.find((f) => f.operator === 'gte');
  const lteFilter = fieldFilters.find((f) => f.operator === 'lte');

  const fromDate = gteFilter ? safeToDate(gteFilter.value) : null;
  const toDate = lteFilter ? safeToDate(lteFilter.value) : null;

  if (!fromDate && !toDate) {
    return null;
  }

  return {
    type: 'custom',
    fromDate,
    toDate,
  };
}

/**
 * Serializes DateRangeFilterValue for storage/transmission
 */
export function serializeDateRange(value: DateRangeFilterValue): SerializableDateRange {
  return {
    type: value.type,
    fromDate: value.fromDate ? formatDateForApiWithoutTimezone(value.fromDate) : null,
    toDate: value.toDate ? formatDateForApiWithoutTimezone(value.toDate) : null,
  };
}

/**
 * Deserializes SerializableDateRange back to DateRangeFilterValue
 */
export function deserializeDateRange(value: SerializableDateRange): DateRangeFilterValue {
  return {
    type: value.type as DateRangeFilterValue['type'],
    fromDate: safeToDate(value.fromDate),
    toDate: safeToDate(value.toDate),
  };
}

/**
 * Safely serializes DataSourceRequestState for API calls
 * Automatically handles date range transformations and prevents [object Object] issues
 */
export function safeSerializeDataState(dataState: DataSourceRequestState): DataSourceRequestState {
  if (!dataState.filter?.filters) {
    return dataState;
  }

  const processFilter = (filter: FilterDescriptor | CompositeFilterDescriptor): FilterDescriptor | CompositeFilterDescriptor | null => {
    if ('filters' in filter) {
      // CompositeFilterDescriptor
      const processedFilters = filter.filters.map(processFilter).filter((f): f is FilterDescriptor | CompositeFilterDescriptor => f !== null);

      return processedFilters.length > 0 ? { ...filter, filters: processedFilters } : null;
    } else {
      // FilterDescriptor
      let { value } = filter;

      // Handle our new serializable date range format
      if (value && typeof value === 'object' && value !== null) {
        const obj = value as Record<string, unknown>;

        if (obj.__isDateRange === true && Array.isArray(obj.filters)) {
          console.warn(`Found serializable date range for field ${String(filter.field)}, expanding to individual filters`);
          const filters = obj.filters as FilterDescriptor[];
          if (filters.length > 0) {
            console.warn(`Using first filter of ${filters.length} date range filters`);
            return filters[0];
          }
          return null;
        }

        // Handle old DateRangeFilterValue objects that shouldn't be here
        if ('type' in obj && typeof obj.type === 'string') {
          console.warn(`Found DateRangeFilterValue in filter for field ${String(filter.field)}, removing invalid filter`);
          return null; // Remove invalid filter
        }
      }

      // Convert dates to ISO strings for API transmission
      if (value instanceof Date) {
        value = value.toISOString();
      } else if (typeof value === 'string' && value.includes('[object Object]')) {
        console.warn(`Removing malformed filter value: ${value}`);
        return null;
      }

      return { ...filter, value };
    }
  };

  const processedFilters = dataState.filter.filters.map(processFilter).filter((f): f is FilterDescriptor | CompositeFilterDescriptor => f !== null);

  return {
    ...dataState,
    filter: {
      ...dataState.filter,
      filters: processedFilters,
    },
  };
}

// Legacy function for backward compatibility
export function serializeDate(value: unknown): string | null {
  const date = safeToDate(value);
  return date ? formatDateForApiWithoutTimezone(date) : null;
}

/**
 * Convert a Date to an ISO string with error handling
 */
export function safeToISOString(value: unknown): string | null {
  const date = safeToDate(value);
  if (!date) {
    return null;
  }

  try {
    return date.toISOString();
  } catch (error) {
    console.warn('safeToISOString - Error converting date to ISO string:', error);
    return null;
  }
}

/**
 * Formats a Date object as an ISO string preserving the local date/time.
 * This prevents timezone conversion issues where "July 8, 2025 00:00" becomes "July 8, 2025 04:00Z" for Eastern timezone users.
 * The result does not include timezone information to prevent server-side timezone conversion.
 */
export function formatDateForApiWithoutTimezone(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const milliseconds = String(date.getMilliseconds()).padStart(3, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
}

/**
 * Check if a value is a Date object (not just Date-like)
 */
export function isDateObject(value: unknown): value is Date {
  return value instanceof Date && !isNaN(value.getTime());
}

/**
 * Check if a value is an ISO date string
 */
export function isISODateString(value: unknown): value is string {
  if (typeof value !== 'string') return false;

  const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;
  return isoRegex.test(value);
}

/**
 * Check if a value might be a DateRangeFilterValue object
 */
function isDateRangeFilterValue(value: unknown): boolean {
  return value != null && typeof value === 'object' && 'type' in value && typeof (value as Record<string, unknown>).type === 'string';
}

/**
 * Serialize date filters properly for API calls
 */
export function serializeDateFiltersForAPI(dataState: State): State {
  if (!dataState?.filter?.filters) {
    return dataState;
  }

  const processFilters = (filters: (FilterDescriptor | CompositeFilterDescriptor)[]): (FilterDescriptor | CompositeFilterDescriptor)[] => {
    return filters
      .map((filter) => {
        if ('filters' in filter) {
          return {
            ...filter,
            filters: processFilters(filter.filters),
          };
        } else {
          let { value } = filter;

          // Convert Date objects to ISO strings for API without timezone conversion
          if (isDateObject(value)) {
            value = formatDateForApiWithoutTimezone(value);
          }
          // Remove DateRangeFilterValue objects that shouldn't be in filters
          else if (isDateRangeFilterValue(value)) {
            console.warn('Found DateRangeFilterValue in filter, removing:', value);
            return null;
          }
          // Check for malformed values
          else if (typeof value === 'string' && value.includes('[object Object]')) {
            console.warn('Found malformed object string in filter, removing:', value);
            return null;
          }

          return { ...filter, value };
        }
      })
      .filter(Boolean) as (FilterDescriptor | CompositeFilterDescriptor)[];
  };

  const processedFilters = processFilters(dataState.filter.filters);

  return {
    ...dataState,
    filter: {
      ...dataState.filter,
      filters: processedFilters,
    },
  };
}

/**
 * Check if a field name is typically a date field
 */
function isDateFieldName(fieldName: string): boolean {
  const dateFields = [
    'date',
    'created',
    'updated',
    'modified',
    'timestamp',
    'time',
    'dob',
    'birthdate',
    'birthday',
    'slaDate',
    'studyDate',
    'uploadedDate',
    'dateCreated',
    'completedDate',
    'requestedDate',
    'assignedDate',
    'returnedDate',
  ];

  const fieldLower = fieldName.toLowerCase();
  return dateFields.some((df) => fieldLower.includes(df));
}

/**
 * Deserialize date filters for UI (convert ISO strings back to Date objects)
 */
export function deserializeDateFiltersForUI(dataState: DataSourceRequestState): DataSourceRequestState {
  if (!dataState?.filter?.filters) {
    return dataState;
  }

  const processFilters = (filters: (FilterDescriptor | CompositeFilterDescriptor)[]): (FilterDescriptor | CompositeFilterDescriptor)[] => {
    return filters.map((filter) => {
      if ('filters' in filter) {
        return {
          ...filter,
          filters: processFilters(filter.filters),
        };
      } else {
        let { value } = filter;

        // Convert ISO strings back to Date objects for date fields
        if (typeof value === 'string' && typeof filter.field === 'string' && isDateFieldName(filter.field) && isISODateString(value)) {
          const date = safeToDate(value);
          if (date) {
            value = date;
          }
        }

        return { ...filter, value };
      }
    });
  };

  const processedFilters = processFilters(dataState.filter.filters);

  return {
    ...dataState,
    filter: {
      ...dataState.filter,
      filters: processedFilters,
    },
  };
}

/**
 * Check if a date value is valid for UI display
 */
export function isValidDateForUI(value: unknown): value is Date {
  return isDateObject(value);
}

/**
 * Get a Date object from any date-like value for UI use
 */
export function getDateForUI(value: unknown): Date | null {
  return safeToDate(value);
}
